/**
 * Simple outline icons for the side nav, one per page. Drawn by hand to match
 * the app's plain, uncluttered look rather than pulled from an icon library.
 */
export const shared = {
  width: '1.1rem',
  height: '1.1rem',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

export function DashboardIcon() {
  return (
    <svg {...shared}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="4.5" rx="1" />
      <rect x="13.5" y="10.5" width="7" height="10" rx="1" />
      <rect x="3.5" y="13" width="7" height="7.5" rx="1" />
    </svg>
  );
}

export function PeopleIcon() {
  return (
    <svg {...shared}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <circle cx="17" cy="9" r="2.3" />
      <path d="M15 20c0-2.3 1.6-4 3.8-4.2" />
    </svg>
  );
}

export function ScheduleIcon() {
  return (
    <svg {...shared}>
      <rect x="3.5" y="4.5" width="17" height="16" rx="1.5" />
      <path d="M3.5 9.5h17" />
      <path d="M8 3v3M16 3v3" />
      <path d="M7.5 13h2.5M11.5 13H14M7.5 16.5h2.5" />
    </svg>
  );
}

export function AssignmentsIcon() {
  return (
    <svg {...shared}>
      <path d="M7 4.5h8l3 3V19a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5.5a1 1 0 0 1 1-1Z" />
      <path d="M15 4.5V8h3" />
      <path d="m9 13 2 2 4-4" />
    </svg>
  );
}

export function MoneyIcon() {
  return (
    <svg {...shared}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5v9M14.5 9.5c0-1.1-1.1-2-2.5-2s-2.5.8-2.5 2c0 2.5 5 1.3 5 3.6 0 1.2-1.1 2.1-2.5 2.1s-2.5-.9-2.5-2" />
    </svg>
  );
}

export function TagsIcon() {
  return (
    <svg {...shared}>
      <path d="M11.5 4h5.5a1 1 0 0 1 1 1v5.5a1 1 0 0 1-.3.7l-8 8a1 1 0 0 1-1.4 0l-5.5-5.5a1 1 0 0 1 0-1.4l8-8a1 1 0 0 1 .7-.3Z" />
      <circle cx="15" cy="8" r="1.3" />
    </svg>
  );
}

export function ChecklistsIcon() {
  return (
    <svg {...shared}>
      <rect x="4.5" y="3.5" width="15" height="17" rx="1.5" />
      <path d="m7.5 8 1.3 1.3L11 7" />
      <path d="M13 8h5.5" />
      <path d="m7.5 14 1.3 1.3L11 13" />
      <path d="M13 14h5.5" />
    </svg>
  );
}

/** An envelope: the wording of the emails and texts the app sends. */
export function TemplatesIcon() {
  return (
    <svg {...shared}>
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
      <path d="m4 6.5 8 6 8-6" />
    </svg>
  );
}

/** A person and a key: who can sign in, to the office or the portal. */
export function UsersIcon() {
  return (
    <svg {...shared}>
      <circle cx="8.5" cy="9" r="3" />
      <path d="M3.5 20c0-3 2.5-5 5-5s5 2 5 5" />
      <circle cx="17.5" cy="7" r="1.6" />
      <path d="M17.5 8.6V13M16 10.5h1.2M16 12h1.2" />
    </svg>
  );
}

/** Sliders: the business's settings. */
export function SettingsIcon() {
  return (
    <svg {...shared}>
      <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="17" r="2" />
    </svg>
  );
}

/** Close: a panel or dialog. */
export function CloseIcon() {
  return (
    <svg {...shared}>
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </svg>
  );
}

/** More: the rest of the menu, on a phone's bottom bar. */
export function MoreIcon() {
  return (
    <svg {...shared}>
      <circle cx="5.5" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

/** Where: a map pin, beside a visit's suburb. */
export function PlaceIcon() {
  return (
    <svg {...shared}>
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.3" />
    </svg>
  );
}

/** Who: one person, beside whoever is on a visit. */
export function PersonIcon() {
  return (
    <svg {...shared}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5" />
    </svg>
  );
}

/** Edit: a pencil, on something that can be changed where it is shown. */
export function EditIcon() {
  return (
    <svg {...shared}>
      <path d="M15.5 5.5l3 3L8 19H5v-3z" />
      <path d="M13.5 7.5l3 3" />
    </svg>
  );
}

/** When: a clock, beside a visit's times. */
export function ClockIcon() {
  return (
    <svg {...shared}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}
